import type { EarTrainingClip } from '../types/earTraining';
import type { EarTrainingInstrument } from './earTrainingPreferences';

function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

type VoiceConfig = {
  types: OscillatorType[];
  detuneCents?: number[];
  gains?: number[];
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  vibratoHz?: number;
  vibratoDepth?: number;
};

const VOICES: Record<EarTrainingInstrument, VoiceConfig> = {
  triangle: {
    types: ['triangle'],
    attack: 0.02,
    decay: 0.05,
    sustain: 0.2,
    release: 0.08,
  },
  piano: {
    types: ['triangle', 'sine'],
    detuneCents: [0, 4],
    gains: [0.55, 0.35],
    attack: 0.005,
    decay: 0.15,
    sustain: 0.12,
    release: 0.2,
  },
  flute: {
    types: ['sine'],
    attack: 0.06,
    decay: 0.08,
    sustain: 0.22,
    release: 0.12,
    vibratoHz: 5.5,
    vibratoDepth: 0.008,
  },
  guitar: {
    types: ['triangle'],
    attack: 0.003,
    decay: 0.12,
    sustain: 0.04,
    release: 0.15,
  },
  clarinet: {
    types: ['square'],
    attack: 0.04,
    decay: 0.06,
    sustain: 0.18,
    release: 0.1,
  },
  organ: {
    types: ['sine', 'triangle'],
    detuneCents: [0, 7],
    gains: [0.4, 0.25],
    attack: 0.02,
    decay: 0.04,
    sustain: 0.28,
    release: 0.06,
  },
};

/** Play a monophonic clip with selectable instrument timbre. */
export class EarTrainingPlayer {
  private ctx: AudioContext | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private nodes: AudioScheduledSourceNode[] = [];

  stop(): void {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    for (const n of this.nodes) {
      try {
        n.stop();
        n.disconnect();
      } catch {
        /* already stopped */
      }
    }
    this.nodes = [];
  }

  /** Play one pitch (e.g. key or starting note hint). Stops any clip in progress. */
  async playNote(
    midi: number,
    instrument: EarTrainingInstrument,
    durationSec = 0.75
  ): Promise<void> {
    this.stop();
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) throw new Error('Web Audio is not supported in this browser.');
    if (!this.ctx) this.ctx = new Ctx();
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const voice = VOICES[instrument];
    const start = this.ctx.currentTime + 0.03;
    this.scheduleNote(midi, start, durationSec, voice);
    this.stopTimer = setTimeout(() => {
      this.stop();
    }, durationSec * 1000 + 80);
  }

  async play(
    clip: EarTrainingClip,
    instrument: EarTrainingInstrument,
    onEnd?: () => void
  ): Promise<void> {
    this.stop();
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) throw new Error('Web Audio is not supported in this browser.');
    if (!this.ctx) this.ctx = new Ctx();
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const voice = VOICES[instrument];
    const beatMs = 60_000 / clip.tempoBpm;
    const transpose = clip.transposeSemitones;
    let t = this.ctx.currentTime + 0.05;

    for (const note of clip.notes) {
      const midi = note.midi + transpose;
      const durSec = (note.beats * beatMs) / 1000;
      this.scheduleNote(midi, t, durSec, voice);
      t += durSec + 0.06;
    }

    const totalMs = (t - this.ctx.currentTime) * 1000;
    this.stopTimer = setTimeout(() => {
      this.stop();
      onEnd?.();
    }, totalMs);
  }

  private scheduleNote(midi: number, start: number, durSec: number, voice: VoiceConfig): void {
    if (!this.ctx) return;
    const hz = midiToHz(midi);
    const master = this.ctx.createGain();
    master.gain.setValueAtTime(0, start);
    master.gain.linearRampToValueAtTime(0.24, start + voice.attack);
    master.gain.linearRampToValueAtTime(voice.sustain * 0.24, start + voice.attack + voice.decay);
    master.gain.setValueAtTime(voice.sustain * 0.24, start + durSec - voice.release);
    master.gain.linearRampToValueAtTime(0, start + durSec);

    voice.types.forEach((type, i) => {
      const osc = this.ctx!.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(hz, start);
      if (voice.vibratoHz && voice.vibratoDepth) {
        const lfo = this.ctx!.createOscillator();
        const lfoGain = this.ctx!.createGain();
        lfo.frequency.value = voice.vibratoHz;
        lfoGain.gain.value = hz * voice.vibratoDepth;
        lfo.connect(lfoGain);
        lfoGain.connect(osc.frequency);
        lfo.start(start);
        lfo.stop(start + durSec + 0.05);
        this.nodes.push(lfo);
      }
      const g = this.ctx!.createGain();
      const level = voice.gains?.[i] ?? 1 / voice.types.length;
      g.gain.value = level;
      osc.connect(g);
      g.connect(master);
      osc.start(start);
      osc.stop(start + durSec + 0.05);
      this.nodes.push(osc);
    });

    if (instrumentUsesLowpass(voice)) {
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 2200;
      master.connect(filter);
      filter.connect(this.ctx.destination);
    } else {
      master.connect(this.ctx.destination);
    }
  }

  dispose(): void {
    this.stop();
    void this.ctx?.close();
    this.ctx = null;
  }
}

function instrumentUsesLowpass(voice: VoiceConfig): boolean {
  return voice.types.includes('square');
}
